import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";

interface Entity {
  label: string;
  type: string;
  x: number;
  y: number;
  color: string;
  delay: number;
  isQuery?: boolean;
}

const ENTITIES: Entity[] = [
  // Query-matched entities (highlighted)
  { label: "Auth System", type: "PROJECT", x: 480, y: 420, color: COLORS.accentBlue, delay: 30, isQuery: true },
  { label: "Session Token", type: "CONCEPT", x: 480, y: 600, color: COLORS.accentPurple, delay: 35, isQuery: true },

  // 1-hop neighborhood (discovered via graph)
  { label: "Supabase Auth", type: "TOOL", x: 280, y: 320, color: COLORS.accentTeal, delay: 80 },
  { label: "JWT", type: "CONCEPT", x: 680, y: 320, color: COLORS.accentPurple, delay: 90 },
  { label: "RLS Policies", type: "CONCEPT", x: 280, y: 540, color: COLORS.accentGreen, delay: 100 },
  { label: "OAuth Flow", type: "CONCEPT", x: 680, y: 540, color: COLORS.accentOrange, delay: 110 },
  { label: "Token Refresh", type: "CONCEPT", x: 480, y: 240, color: COLORS.accentPink, delay: 120 },
];

interface Edge {
  from: number;
  to: number;
  label: string;
  delay: number;
}

const EDGES: Edge[] = [
  { from: 0, to: 2, label: "USES", delay: 85 },
  { from: 0, to: 3, label: "IMPLEMENTS", delay: 95 },
  { from: 0, to: 4, label: "ENFORCES", delay: 105 },
  { from: 1, to: 3, label: "STORED_AS", delay: 100 },
  { from: 1, to: 5, label: "PART_OF", delay: 115 },
  { from: 1, to: 6, label: "TRIGGERS", delay: 125 },
  { from: 6, to: 3, label: "PRODUCES", delay: 130 },
];

export const Scene5GraphAugment: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  const exitOpacity = interpolate(frame, [210, 240], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.05} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 50,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
        }}
      >
        <div style={{ fontSize: 42, fontFamily: FONTS.heading, fontWeight: 700, color: COLORS.textPrimary }}>
          Step 5:{" "}
          <span style={{ color: COLORS.accentGreen }}>Knowledge-Graph Augmentation</span>
        </div>
        <div style={{ fontSize: 18, fontFamily: FONTS.body, color: COLORS.textSecondary, marginTop: 8 }}>
          Match entities in query → Fetch 1-hop neighborhood → Enrich the kept chunks
        </div>
      </div>

      {/* Graph area — left half */}
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          overflow: "visible",
          pointerEvents: "none",
        }}
      >
        {/* Edges */}
        {EDGES.map((edge, i) => {
          const fromNode = ENTITIES[edge.from];
          const toNode = ENTITIES[edge.to];
          const edgeAppear = spring({ frame: frame - edge.delay, fps, config: SPRING.gentle });
          const progress = interpolate(edgeAppear, [0, 1], [0, 1]);
          const endX = fromNode.x + (toNode.x - fromNode.x) * progress;
          const endY = fromNode.y + (toNode.y - fromNode.y) * progress;
          const midX = (fromNode.x + toNode.x) / 2;
          const midY = (fromNode.y + toNode.y) / 2;

          return (
            <React.Fragment key={i}>
              <line
                x1={fromNode.x}
                y1={fromNode.y}
                x2={endX}
                y2={endY}
                stroke={fromNode.color}
                strokeWidth={1.5}
                opacity={interpolate(edgeAppear, [0, 0.3], [0, 0.5])}
                strokeDasharray="4 4"
              />
              {progress > 0.8 && (
                <text
                  x={midX}
                  y={midY - 8}
                  textAnchor="middle"
                  fill={COLORS.textMuted}
                  fontSize={10}
                  fontFamily={FONTS.mono}
                  opacity={interpolate(progress, [0.8, 1], [0, 0.7])}
                >
                  {edge.label}
                </text>
              )}
            </React.Fragment>
          );
        })}
      </svg>

      {/* Entity nodes */}
      {ENTITIES.map((entity, i) => {
        const nodeAppear = spring({ frame: frame - entity.delay, fps, config: SPRING.bouncy });
        const scale = interpolate(nodeAppear, [0, 1], [0, 1]);
        const opacity = interpolate(nodeAppear, [0, 1], [0, 1]);
        const floatY = Math.sin((frame + i * 20) * 0.03) * 3;

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: entity.x,
              top: entity.y + floatY,
              transform: `translate(-50%, -50%) scale(${scale})`,
              opacity,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
            }}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: `radial-gradient(circle at 40% 35%, ${entity.color}30, ${entity.color}10)`,
                border: `2px solid ${entity.isQuery ? entity.color : `${entity.color}50`}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: entity.isQuery ? `0 0 25px ${entity.color}30` : "none",
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 9,
                  color: entity.color,
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                {entity.type}
              </span>
            </div>
            <span
              style={{
                fontFamily: FONTS.mono,
                fontSize: 12,
                color: entity.isQuery ? COLORS.textPrimary : COLORS.textSecondary,
                fontWeight: entity.isQuery ? 600 : 400,
              }}
            >
              {entity.label}
            </span>
            {entity.isQuery && (
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 10,
                  color: COLORS.accentBlue,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: `${COLORS.accentBlue}15`,
                }}
              >
                MATCHED
              </span>
            )}
          </div>
        );
      })}

      {/* Right panel — enrichment results */}
      <div
        style={{
          position: "absolute",
          right: 100,
          top: 200,
          width: 480,
        }}
      >
        {/* Before */}
        {(() => {
          const beforeAppear = spring({ frame: frame - 50, fps, config: SPRING.snappy });
          return (
            <div
              style={{
                opacity: interpolate(beforeAppear, [0, 1], [0, 1]),
                marginBottom: 30,
              }}
            >
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 13,
                  color: COLORS.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: 1.5,
                  marginBottom: 10,
                }}
              >
                Before: Search Results Only
              </div>
              <div
                style={{
                  padding: "14px 20px",
                  borderRadius: 10,
                  background: COLORS.bgCard,
                  border: `1px solid ${COLORS.borderLight}`,
                  fontFamily: FONTS.mono,
                  fontSize: 14,
                  color: COLORS.textSecondary,
                }}
              >
                5 chunks from hybrid search (may miss implicit context)
              </div>
            </div>
          );
        })()}

        {/* Arrow down */}
        <div
          style={{
            textAlign: "center",
            fontSize: 24,
            color: COLORS.accentGreen,
            margin: "10px 0",
            opacity: interpolate(
              spring({ frame: frame - 140, fps, config: SPRING.snappy }),
              [0, 1],
              [0, 1],
            ),
          }}
        >
          ↓ + graph neighborhood
        </div>

        {/* After */}
        {(() => {
          const afterAppear = spring({ frame: frame - 150, fps, config: SPRING.snappy });
          return (
            <div
              style={{
                opacity: interpolate(afterAppear, [0, 1], [0, 1]),
                marginTop: 10,
              }}
            >
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 13,
                  color: COLORS.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: 1.5,
                  marginBottom: 10,
                }}
              >
                After: Graph-Enriched Results
              </div>
              {[
                { chunk: "supabase-auth.ts:handleSession()", source: "search", color: COLORS.accentTeal },
                { chunk: "jwt.ts:verifyToken()", source: "search", color: COLORS.accentTeal },
                { chunk: "oauth-flow.ts:refreshToken()", source: "graph", color: COLORS.accentGreen },
                { chunk: "rls-enforcer.ts:setTenant()", source: "search", color: COLORS.accentTeal },
                { chunk: "token-store.ts:sessionExpiry", source: "graph", color: COLORS.accentGreen },
              ].map((item, i) => {
                const itemAppear = spring({
                  frame: frame - 155 - i * 8,
                  fps,
                  config: SPRING.snappy,
                });
                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 16px",
                      marginBottom: 6,
                      borderRadius: 8,
                      background: `${item.color}08`,
                      border: `1px solid ${item.color}25`,
                      opacity: interpolate(itemAppear, [0, 1], [0, 1]),
                      transform: `translateX(${interpolate(itemAppear, [0, 1], [15, 0])}px)`,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: FONTS.mono,
                        fontSize: 13,
                        color: COLORS.textSecondary,
                        flex: 1,
                      }}
                    >
                      {item.chunk}
                    </span>
                    <span
                      style={{
                        fontFamily: FONTS.mono,
                        fontSize: 10,
                        color: item.color,
                        padding: "2px 8px",
                        borderRadius: 4,
                        background: `${item.color}15`,
                        fontWeight: 600,
                        textTransform: "uppercase",
                      }}
                    >
                      {item.source}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Bottom stat */}
      {(() => {
        const statAppear = spring({ frame: frame - 190, fps, config: SPRING.snappy });
        return (
          <div
            style={{
              position: "absolute",
              bottom: 70,
              left: "50%",
              transform: "translate(-50%, 0)",
              opacity: interpolate(statAppear, [0, 1], [0, 1]),
              fontFamily: FONTS.mono,
              fontSize: 15,
              color: COLORS.textMuted,
            }}
          >
            Max <span style={{ color: COLORS.accentGreen, fontWeight: 700 }}>3 entities</span> matched per query ·
            1-hop neighborhood · Chunks merged with search results
          </div>
        );
      })()}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
